import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import * as faceapi from '@vladmandic/face-api';
import { CameraPermissionService, PermissionStatus } from 'src/app/services/camera-permission.service';
import { FaceapiService } from 'src/app/services/faceapi/faceapi.service';
import { BrightnessThreshold, FaceMeshService, MouthThreshold, PupilThreshold  } from 'src/app/services/face-mesh/face-mesh.service';
import { RecognizingValidationsImagesService } from 'src/app/services/recognizing-validations/recognizing-validations-images.service';
import { AnimationOptions } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { AmplitudeEvent, FaceRegistrationStarted, Platform, ScanType, UnsuccesfulFaceRegistration, VisitType } from 'src/app/interface/amplitude.interface';
import { AmplitudeService } from 'src/app/services/amplitude.service';
import { UserService } from 'src/app/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';


export enum RecognitionSteps {
  PreviewRecognition = "PreviewRecognition",    //Step1
  Recomendations = "Recomendations",           //Step 2
  Recognizing = "Recognizing",                //Step 3
  RecognitionFailed = "RecognitionFailed",    //Step 4
  RecognitionSuccessed = "RecognitionSuccessed",//Step 5
};

export enum RecognitionErrors {
  Recognition = "Recognition",
  Permissions = "Permissions",
  Camera = "Camera"
};

export enum Invalidations{
  LowLighting = "lowLighting",
  Positioning = "positioning",
  Distancing = 'distancing',
  InsufficientMovement = "insufficientMovement",
  MuchMovement = "muchMovement",
  Expression = "expression",
  EyesContact = "eyesContact",
  Default = "default"
}

export enum InvalidationsOnCapture{
  NotCentered = "NotCentered",
  NotEyesContact = "NotEyesContact",
  FarAway = "FarAway",
  VeryExpressive = "VeryExpressive",
  LowLighting = 'LowLighting'
}

const slideTransition = (isForward: boolean) => {
  const leaveTo = isForward ? '-100%' : '100%'; // Hacia dónde se va el elemento que sale
  const enterFrom = isForward ? '100%' : '-100%'; // Desde dónde entra el nuevo elemento

  return [
    // Estilos para los elementos que entran y salen durante la animación
    query(':enter, :leave', [
      style({
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%'
      })
    ], { optional: true }),
    query(':enter', [
      style({ transform: `translateX(${enterFrom})`, opacity: 0.8 })
    ], { optional: true }),
    group([
      query(':leave', [
        animate('400ms ease-in-out', style({ transform: `translateX(${leaveTo})`, opacity: 0 }))
      ], { optional: true }),
      query(':enter', [
        animate('400ms ease-in-out', style({ transform: 'translateX(0%)', opacity: 1 }))
      ], { optional: true })
    ])
  ];
};

export function getStepOrder(step: RecognitionSteps): number {
  switch (step) {
    case RecognitionSteps.PreviewRecognition: return 0;
    case RecognitionSteps.Recomendations: return 1;
    case RecognitionSteps.Recognizing: return 2;
    case RecognitionSteps.RecognitionSuccessed: return 3;
    case RecognitionSteps.RecognitionFailed: return 3;
    default: return -1;
  }
}

@Component({
  selector: 'shared-face-recognition-modal',
  templateUrl: './face-recognition-modal.component.html',
  styleUrls: ['./face-recognition-modal.component.scss'],
  animations: [
    trigger('slideAnimation', [
      // Transición hacia adelante
      transition((fromState: string, toState: string) => {
        const fromStep = fromState as RecognitionSteps;
        const toStep = toState as RecognitionSteps;
        return getStepOrder(toStep) > getStepOrder(fromStep);
      }, slideTransition(true)),

      // Transición hacia atrás
      transition((fromState: string, toState: string) => {
        const fromStep = fromState as RecognitionSteps;
        const toStep = toState as RecognitionSteps;
        return getStepOrder(toStep) < getStepOrder(fromStep);
      }, slideTransition(false)),
    ])
  ]
})
export class FaceRecognitionModalComponent implements OnInit, OnDestroy {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasElement!: ElementRef<HTMLCanvasElement>;
  @Input() formGuest: FormGroup;
  @Input() userData: any;
  @Input() userImage: string;
  @Input() registrationCompleted: boolean;
  @Output() urlPhoto: EventEmitter<any> = new EventEmitter();
  @Output() faceData: EventEmitter<{bestImageBlob: Blob, bestImage: string, bestDescriptor: Float32Array}> = new EventEmitter();
  @Output() closeModal: EventEmitter<boolean> = new EventEmitter();
  // Form control for photo capture
  photoControl = new FormControl();
  photoCaptured = false;

  // Recognition steps and errors
  recognitionSteps = RecognitionSteps;
  recognitionStep = RecognitionSteps.PreviewRecognition;
  errorType: RecognitionErrors | undefined = RecognitionErrors.Recognition;

  // Scan and animation settings
  totalLines = 50;                                // Total de líneas de escaneo (puedes cambiar este número)
  scanLines = Array(this.totalLines).fill(0);
  animationProgress: number = 0;
  activeScanLines = 0;
  scanInterval: any;
  animationInterval: any;
  scanLineAnimationInterval:any = null;
  private _destroyed = false;
  private _noDetectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private _collectTimeout: ReturnType<typeof setTimeout> | null = null;
  private _detectionStartTimeout: ReturnType<typeof setTimeout> | null = null;
  private _cameraInitTimeout: ReturnType<typeof setTimeout> | null = null;
  scanTime: number = 10;                           //Representa los segundos que dura el escaneo
  maxCollectSeconds:number = 10;
  minCapturesValidDetections:number = 10;
  detectionsPerSecond:number = 1;
  suggestionText: string = 'faceRecognition.waitForTheScan';

  // Recognition results
  averageDescriptor: any;
  descriptors: Float32Array[] = [];               //Iran todas las detecciones validas o invalidas
  detections:faceapi.FaceDetection[] = [];
  skinScores:number[] = [];
  validDescriptors: Float32Array[] = [];
  bestDescriptor: Float32Array | null = null;     // El descriptor más confiable
  bestImage: string | null = null;
  bestImageCanvas:any = null;
  bestImageBlob: Blob | null = null;
  gettingDescriptor:boolean = false;

  // Face position and movement tracking
  lastFacePosition: { x: number, y: number } | null = null;
  framesWithoutMovement: number = 0;
  maxFramesWithoutMovement: number = 1;
  movementThreshold: number = 3;
  maxFrames: number = 10;
  modelsLoaded: boolean = false;
  modelsWarmed:boolean = false;

  // Video settings
  scanning: boolean = false;
  cameraLoading = true;
  cameraReady: boolean = false;
  videoHeight: number = 350;
  videoWidth: number = 350;
  availableCameras: MediaDeviceInfo[] = [];
  camerasChecked: boolean = false;
  selectedCameraId?: string;
  currentStream?: MediaStream;
  lastFaceBox:any = null;

  //Thresholds
  skinTextureThreshold:number = 500;
  confidenceThreshold:number = 0.80;
  minFaceSizeThreshold:number = 0.15; //0.30
  neutralExpresionThreshold:number = 0.50;
  generalDeephThreshold:number = 0.000125;
  centeringVariance:number = 0.30; //0.15

  //Errors - Invalidations
  invalidations:{error:Invalidations, value:any}[] = [];
  invalidationsOnCapture:{error:InvalidationsOnCapture, times:number}[] = [];
  currentInvalidationsOnCapture:{notCentered:boolean, veryExpressive:boolean, farAway:boolean} = {notCentered:false, veryExpressive:false, farAway:false};
  
  // Animation settings
  animationOptions: AnimationOptions = {
    path: '/assets/animations/Maia-animation.json',
    autoplay: true,
    name: "loading",
  };

  constructor(
    private cameraPermissionService: CameraPermissionService,
    private faceMeshService: FaceMeshService,
    private faceapiService: FaceapiService,
    private imagesValidationsService : RecognizingValidationsImagesService,
    private amplitudeService: AmplitudeService,
    private userService: UserService,
    private toastr: ToastrService,
    private translateService: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    try {
      if (!this.faceapiService.modelsLoaded) {
        await this.faceapiService.loadModels();
      }
      if (!this.faceMeshService.faceMeshLoaded) {
        this.faceMeshService.initFaceMesh();
      }
      this.modelsLoaded = this.faceapiService.modelsLoaded && this.faceMeshService.faceMeshLoaded;
    } catch (err) {
      this.toastr.error(this.translateService.instant('faceRecognition.errors.errorLoadingFaceModels'));
    }
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._clearAllTimers();
    this._releaseMediaResources();
    this.scanning = false;
  }

  private _clearAllTimers(): void {
    if (this._noDetectionTimeout) {
      clearTimeout(this._noDetectionTimeout);
      this._noDetectionTimeout = null;
    }
    if (this._collectTimeout) {
      clearTimeout(this._collectTimeout);
      this._collectTimeout = null;
    }
    if (this._detectionStartTimeout) {
      clearTimeout(this._detectionStartTimeout);
      this._detectionStartTimeout = null;
    }
    if (this._cameraInitTimeout) {
      clearTimeout(this._cameraInitTimeout);
      this._cameraInitTimeout = null;
    }
    clearInterval(this.scanInterval);
    this.scanInterval = null;
    clearInterval(this.animationInterval);
    this.animationInterval = null;
    clearInterval(this.scanLineAnimationInterval);
    this.scanLineAnimationInterval = null;
  }

  private _releaseMediaResources(): void {
    const video = this.videoElement?.nativeElement;
    const stream = video?.srcObject as MediaStream | null | undefined;
    stream?.getTracks().forEach((track) => track.stop());
    if (video) {
      video.srcObject = null;
    }
  }

  // Animation control
  animationCreated(animationItem: AnimationItem): void {
    animationItem.setSpeed(1);
  }

  async onCameraSelected(deviceId: string): Promise<void> {
    this.camerasChecked = true;
    this.selectedCameraId = deviceId;
    if(this.selectedCameraId){
      this.recognitionStep = RecognitionSteps.Recognizing;
      this.faceapiService.warmupDetection(3).then(()=>{
        this.modelsLoaded = this.faceapiService.modelsLoaded && this.faceMeshService.faceMeshLoaded;
        this.modelsWarmed = this.faceapiService.modelsWarmed;
        this.startRecognition();
      });
    }else{
      this.errorType = RecognitionErrors.Camera;
      this.recognitionStep = RecognitionSteps.RecognitionFailed;
    }
  }

  advanceScanAnimation() {
    const linesPerDetection = this.totalLines / this.minCapturesValidDetections;
    this.animationProgress += linesPerDetection;
    const targetLines = Math.floor(this.animationProgress);

    if (this.scanLineAnimationInterval) {
      return;
    }

    this.scanLineAnimationInterval = setInterval(() => {
      if (this.activeScanLines < targetLines && this.activeScanLines < this.totalLines) {
        this.activeScanLines++;
      } else {
        clearInterval(this.scanLineAnimationInterval);
        this.scanLineAnimationInterval = null;
      }
    }, 100);
  }

  // Start face recognition process
  async startRecognition() {
    this.recognitionStep = this.recognitionSteps.Recognizing;
    const mediaConfig: MediaStreamConstraints = {
      video: {
        deviceId: { exact: this.selectedCameraId },
        frameRate: { ideal: 30 },
        advanced:[
          { width: { exact: 1280 }, height: { exact: 720 }, frameRate: { ideal: 30 } },
          { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
        ]
      },
      audio: false
    };
  
    try {
      const stream = await navigator.mediaDevices.getUserMedia(mediaConfig);
      if (this._destroyed) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const settigns = stream.getTracks()[0].getSettings();
      this.faceMeshService.getDynamicBlinkThreshold(settigns.width,settigns.height);
      this.cameraReady = true;
      this.cameraLoading = false;
      this._cameraInitTimeout = setTimeout(async () => {
        if (this._destroyed) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        await this.initializeVideoStream(stream);
        this.startDetectionProcess();
      }, 100);
    } catch (error) {
      if (this._destroyed) return;
      this.errorType = RecognitionErrors.Camera;
      this.recognitionStep = RecognitionSteps.RecognitionFailed;
      console.error('Error accessing camera:', error);
    }
  }

  private async initializeVideoStream(stream: MediaStream) {
    if (!this.videoElement?.nativeElement) return;
    this.videoElement.nativeElement.height = this.videoHeight;
    this.videoElement.nativeElement.width = this.videoWidth;
    this.videoElement.nativeElement.srcObject = stream;
    this.videoElement.nativeElement.play();
  }

  private startDetectionProcess() {
    this._detectionStartTimeout = setTimeout(() => {
      if (this._destroyed) return;
      this.amplitudeService.sendToAmplitude({
        event_type: AmplitudeEvent.faceRegistrationStarted,
        user_id: this.userService.getUser().id,
        event_properties: {
          'Tipo de Escaneo': ScanType.visit,
          'Tipo de Visita': this.formGuest.getRawValue().personInvitationType?.value ?? null,
          'Plataforma':Platform.dashboardVigilant
        } as FaceRegistrationStarted
      });
      this.analyzeFrame();
    }, 300);
  }

  private async analyzeFrame(): Promise<void> {
    if (this._destroyed) return;

    const totalSeconds: number = this.scanTime;
    const maxCollectSeconds: number = this.maxCollectSeconds;
    const minCapturesValidDetections: number = this.minCapturesValidDetections;

    let validDetections = 0;
    let firstValidDetected = false;
    this.scanning = true;

    this._noDetectionTimeout = setTimeout(() => {
      if (!this._destroyed) this.stopScanning();
    }, totalSeconds * 1000);

    this.scanInterval = setInterval(async () => {
      if (this._destroyed) return;
      const detectionSuccess = await this.performDetection();
      if (detectionSuccess) {
        validDetections++;

        if (!firstValidDetected) {
          firstValidDetected = true;
          if (this._noDetectionTimeout) {
            clearTimeout(this._noDetectionTimeout);
            this._noDetectionTimeout = null;
          }

          this._collectTimeout = setTimeout(() => {
            if (!this._destroyed) this.stopScanning();
          }, maxCollectSeconds * 1000);
        }

        if (validDetections >= minCapturesValidDetections) {
          if (this._collectTimeout) {
            clearTimeout(this._collectTimeout);
            this._collectTimeout = null;
          }
          this.stopScanning();
        }
      }
    }, 1000);
  }

  // Perform face detection
    async performDetection(): Promise<boolean> {
    if (this._destroyed || !this.videoElement?.nativeElement) return false;
    const detection = await this.faceapiService.detectFaceWithoutDescriptors(this.videoElement.nativeElement);
    if (detection) {
      this.detections.push(detection.detection);
      if (await this.validateDetection(detection)) {
        this.resetCurrentInvalidations();
        this.capturePhoto();
        this.advanceScanAnimation();
        return true; 
      }
      return false;
    } else {
      return false;
    }
  }

  // Stop scanning process
  stopScanning() {
    if (this._destroyed) return;

    if (this._noDetectionTimeout) {
      clearTimeout(this._noDetectionTimeout);
      this._noDetectionTimeout = null;
    }
    if (this._collectTimeout) {
      clearTimeout(this._collectTimeout);
      this._collectTimeout = null;
    }
    clearInterval(this.scanInterval);
    this.scanInterval = null;
    this.scanning = false;
    clearInterval(this.animationInterval);
    this.animationInterval = null;
    clearInterval(this.scanLineAnimationInterval);
    this.scanLineAnimationInterval = null;
    this.averageDescriptor = this.averageDescriptors(this.validDescriptors);
    this.stopCamera(true);
  }

  // Capture photo from video
  capturePhoto() {
    const landmarks = this.faceMeshService.getLandmarks();
    if (
        !this.faceMeshService.acceptableExpressionForCapture || 
        // !this.faceMeshService.isFrontalFace(landmarks[landmarks.length - 1]) || 
        !this.faceMeshService.isLookingAtCamera(landmarks[landmarks.length - 1])
      ) {
        // if(!this.faceMeshService.isFrontalFace(landmarks[landmarks.length - 1])) console.log('No acepto el angulo del rostro, debe ser frontal');
        if(!this.faceMeshService.isLookingAtCamera(landmarks[landmarks.length - 1]) || !this.faceMeshService.acceptableExpressionForCapture){
          this.logInvalidationsOnCapture(InvalidationsOnCapture.NotEyesContact);
        }
      return;
    }

    const video = this.videoElement?.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');

    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

      // Validar textura de piel
      const textureScore = this.imagesValidationsService.analyzeSkinTexture(imageData, video.videoHeight, video.videoWidth);
      const acceptedPartialDeeph = this.faceMeshService.getPartialDeephs();
      acceptedPartialDeeph[acceptedPartialDeeph.length - 1] = acceptedPartialDeeph[acceptedPartialDeeph.length - 1] && (textureScore > this.skinTextureThreshold);
      this.skinScores.push(textureScore);

      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      this.photoControl.setValue(imageDataUrl);
      this.bestImage = imageDataUrl;
      this.bestImageCanvas = canvas;
    }
  }


  // Validate face detection
  async validateDetection(detection: any): Promise<boolean> {
    const { box } = detection.detection;
    this.lastFaceBox = box;
    const { expressions } = detection;
    //Primer conjunto de validaciones a tomar en cuenta (Posicionamiento de rostro)
    const isHighConfidence = detection.detection.score >= this.confidenceThreshold;   //Confianza Minima del resultado
    const isCentered = this.isCenterValidation(box);
    // const isLargeEnough = this.isLargeEnoughValidation(box);
    const isLargeEnough = true;
    // const isNeutralExpression = this.isNeutralExpressionValidation(expressions);
    const isNeutralExpression = true;
    const correctFraming = isHighConfidence && isCentered && isLargeEnough && isNeutralExpression;
    //Nueva validación de profundidad con MediaPipe
    const dynamicValidations = await this.faceMeshService.isRealFaceWithDepth(correctFraming, this.videoElement);
    if(!!dynamicValidations?.acceptableBrightness) this.logInvalidationsOnCapture(InvalidationsOnCapture.LowLighting);
    this.setSuggestionText(isHighConfidence, isCentered, isLargeEnough, isNeutralExpression);
    return correctFraming;
  }

  // Center validation
  isCenterValidation(box: any): boolean {
    // Coordenadas del centro del rostro
    const centerX = box.x + box.width / 2;
    const centerY = box.y + box.height / 2;
    // Coordenadas del centro del video
    const videoCenterX = this.videoElement?.nativeElement.videoWidth / 2;
    const videoCenterY = this.videoElement?.nativeElement.videoHeight / 2;
    // Tolerancia de centrado (15% del tamaño del video)
    const toleranceX = this.videoElement?.nativeElement.videoWidth * this.centeringVariance;
    const toleranceY = this.videoElement?.nativeElement.videoHeight * this.centeringVariance;
    // console.log("centrado de la cara en el cuadro  " + (centerX - videoCenterX), toleranceX,(centerY - videoCenterY), toleranceY)
    return Math.abs(centerX - videoCenterX) <= toleranceX &&
      Math.abs(centerY - videoCenterY) <= toleranceY;
  }

  // Size validation
  isLargeEnoughValidation(box: any): boolean {
    const minFaceSize = this.minFaceSizeThreshold;
    // console.log("Tamaño de la cara en el cuadro  " + box.width, box.height,this.videoElement?.nativeElement.videoWidth, this.videoElement?.nativeElement.videoHeight)
    return box.width >= this.videoElement?.nativeElement.videoWidth * minFaceSize &&
      box.height >= this.videoElement?.nativeElement.videoHeight * minFaceSize;
  }

  // Expression validation
  isNeutralExpressionValidation(expressions: faceapi.FaceExpressions): boolean {
    const neutralThreshold = this.neutralExpresionThreshold;     // Umbral para considerar una expresión como neutra
    const { neutral, happy, angry, surprised, disgusted, fearful } = expressions;
    const maxOther = Math.max(happy, angry, surprised, disgusted, fearful);
    return neutral > neutralThreshold && neutral > maxOther;
  }

  // Anti-spoofing validation
  antiSpoofingValidation(){
    const landmarks = this.faceMeshService.getLandmarks();
    const blinkedTimes = this.faceMeshService.getHowManyTimesBlinked();
    const pupilVariation = this.faceMeshService.calculatePupilMovement(landmarks);
    const mouthVariation = this.faceMeshService.calculateMouthMovement(landmarks);
    const partialPassed = this.faceMeshService.validatePartialDeep();
    const generalPassed = this.faceMeshService.validateGeneralDeep();

    if((pupilVariation < PupilThreshold) && (mouthVariation < MouthThreshold) && (blinkedTimes == 0)){
      this.logInvalidations(Invalidations.InsufficientMovement,`${pupilVariation} //  ${mouthVariation}`);
    }
    if(!partialPassed || (pupilVariation < PupilThreshold) && (mouthVariation < MouthThreshold) && (blinkedTimes == 0)){
      this.amplitudeService.sendToAmplitude({
        event_type: AmplitudeEvent.unsuccesfulFaceRegistration,
        user_id: this.userService.getUser().id,
        event_properties: {
          'imageCaptured': !!this.bestImage,
          'blinkedTimes':blinkedTimes,
          'partialPassed':partialPassed,
          'texturesScores': JSON.stringify(this.skinScores),
          'texturesPassed': this.calculateAverage(this.skinScores) > this.skinTextureThreshold,
          'brightnessScores':JSON.stringify(this.faceMeshService.brightnessScores),
          'brightnessPassed' : this.calculateAverage(this.faceMeshService.brightnessScores) > BrightnessThreshold,
          'generalPassed':generalPassed,
          'pupilVariationPassed': pupilVariation > PupilThreshold,
          'mouthVariationPassed': mouthVariation > MouthThreshold,
          'pupilVariation':pupilVariation,
          'mouthVariation':mouthVariation
        } as UnsuccesfulFaceRegistration
      });
    }

    return ((partialPassed && generalPassed) && ((pupilVariation > PupilThreshold) || (mouthVariation > MouthThreshold) || (blinkedTimes > 0)));
  }

  logInvalidations(error:Invalidations, value:any){
    const index = this.invalidations.findIndex((e)=>e.error == error);
    if(index !== -1) return;
    else{ 
      this.invalidations.push({ error, value });
    }
  }

  // Set suggestion text based on validation results
  setSuggestionText(isHighConfidence: boolean, isCentered: boolean, isLargeEnough: boolean, isNeutralExpression: boolean) {
    let suggestionText: string = 'faceRecognition.errors.invalidationsOnCapture.isNotHighConfidence';
    if (!isHighConfidence) suggestionText = 'faceRecognition.errors.invalidationsOnCapture.isNotHighConfidence';
    if (!isNeutralExpression){
      suggestionText = 'faceRecognition.errors.invalidationsOnCapture.isNotANeutralExpression';
      this.currentInvalidationsOnCapture.veryExpressive = true;
      this.logInvalidationsOnCapture(InvalidationsOnCapture.VeryExpressive);
    }
    if (!isCentered){ 
      suggestionText = 'faceRecognition.errors.invalidationsOnCapture.isNotCentered';
      this.currentInvalidationsOnCapture.notCentered = true;
      this.logInvalidationsOnCapture(InvalidationsOnCapture.NotCentered);
    }
    if (!isLargeEnough){ 
      suggestionText = 'faceRecognition.errors.invalidationsOnCapture.isNotLargeEnough';
      this.currentInvalidationsOnCapture.farAway = true;
      this.logInvalidationsOnCapture(InvalidationsOnCapture.FarAway);
    }
    this.suggestionText = suggestionText;
  }

  logInvalidationsOnCapture(error:InvalidationsOnCapture){
    const index = this.invalidationsOnCapture.findIndex((e)=>e.error == error);
    if(index !== -1) this.invalidationsOnCapture[index].times++;
    else this.invalidationsOnCapture.push({ error, times:1 });
  }

  // Reset recognition process
  resetRecognition() {
    if (this.recognitionStep  != this.recognitionSteps.RecognitionFailed) this.stopCamera(false);

    this.descriptors = [];
    this.detections = [];
    this.skinScores = [];
    this.validDescriptors = [];
    this.activeScanLines = 0;
    this.animationProgress = 0;
    this.photoCaptured = false;
    this.photoControl.reset();
    this.recognitionStep = RecognitionSteps.PreviewRecognition;
    this.faceMeshService.cleanVariables();
    this.bestImage = null;
    this.bestDescriptor = null;
    this.invalidationsOnCapture = [];
    this.invalidations = [];
    this.currentInvalidationsOnCapture = {notCentered:false, veryExpressive:false, farAway:false};
    this.cameraLoading = true;
  }

  // Stop camera and handle recognition result
  async stopCamera(captured: boolean) {
    if (this._destroyed) return;

    const stream = this.videoElement?.nativeElement?.srcObject as MediaStream | null | undefined;
    stream?.getTracks().forEach((track) => track.stop());
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }

    if (this._destroyed) return;
    if (captured && !!this.bestImage) {
    // if (captured && !!this.bestImage && antiSpoofingValidation) {
      this.bestImageBlob = this.dataURLtoBlob(this.bestImage);
      this.gettingDescriptor = true;
      this.photoCaptured = true;
      const detection = await this.faceapiService.detectFaceWithDescriptors(this.bestImageCanvas);
      this.gettingDescriptor = false;
      if (this._destroyed) return;
      if(detection?.descriptor){
        this.bestDescriptor = detection?.descriptor;
        this.recognitionStep = RecognitionSteps.RecognitionSuccessed;
        this.register();
      }else{
        this.recognitionStep = RecognitionSteps.RecognitionFailed;
        this.errorType = RecognitionErrors.Recognition;
      }
    } else {
      this.invalidationsOnCapture.forEach((invalidation)=>{
        if(invalidation.times >= 3){
          switch(invalidation.error){
            case InvalidationsOnCapture.VeryExpressive:
              this.logInvalidations(Invalidations.Expression, true);
            break;
            case InvalidationsOnCapture.NotEyesContact:
              this.logInvalidations(Invalidations.EyesContact, true);
            break;
            case InvalidationsOnCapture.FarAway:
              this.logInvalidations(Invalidations.Distancing, true);
            break
            case InvalidationsOnCapture.NotCentered:
              this.logInvalidations(Invalidations.Positioning, true);
            break;
            case InvalidationsOnCapture.LowLighting:
              this.logInvalidations(Invalidations.LowLighting, true);
            break;
          }
        }
      });
      if(this.invalidations.length == 0){
        this.logInvalidations(Invalidations.Default, true);
      }
      this.amplitudeService.sendToAmplitude({
        event_type: AmplitudeEvent.unsuccesfulFaceRegistration,
        user_id: this.userService.getUser().id,
        event_properties: {
          'imageCaptured': !!this.bestImage,
          'texturesScores': JSON.stringify(this.skinScores),
          'texturesPassed': this.calculateAverage(this.skinScores) > this.skinTextureThreshold,
          'brightnessScores':JSON.stringify(this.faceMeshService.brightnessScores),
          'brightnessPassed' : this.calculateAverage(this.faceMeshService.brightnessScores) > BrightnessThreshold,
        } as UnsuccesfulFaceRegistration
      });
      this.errorType = RecognitionErrors.Recognition;
      this.recognitionStep = RecognitionSteps.RecognitionFailed;
    }
    this.scanning = false;
    this.cameraReady = false;
    this.cameraLoading = true;
  }

  // Average descriptors
  averageDescriptors(descriptors: Float32Array[]): Float32Array {
    if (descriptors.length === 0) return new Float32Array(128);

    const sum = descriptors.reduce((acc, desc) => {
      desc.forEach((value, index) => {
        acc[index] += value;
      });
      return acc;
    }, new Float32Array(128));

    return sum.map((value) => value / descriptors.length);  // Divide cada valor entre la cantidad total de descriptores
  }

  // Check if descriptor is better
  isDescriptorBetter(descriptor: Float32Array, currentBest: Float32Array): boolean {
    const distanceToCurrentBest = this.calculateEuclideanDistance(descriptor, currentBest);
    return distanceToCurrentBest < 0.85;
  }

  // Calculate Euclidean distance between two vectors
  calculateEuclideanDistance(vec1: Float32Array, vec2: Float32Array): number {
    return Math.sqrt(vec1.reduce((sum, value, index) => sum + Math.pow(value - vec2[index], 2), 0));
  }

  // Complete registration
  register() {
    if (this.bestDescriptor && this.bestImage && this.bestImageBlob) {
      this.faceData.emit({ bestDescriptor: this.bestDescriptor, bestImage: this.bestImage, bestImageBlob: this.bestImageBlob });
      this.close();
    }
  }

  // Convert Base64 to Blob
  dataURLtoBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  // Cancel registration
  cancelRegistration() {
    this.resetRecognition();
    this.close();
  }

  // Reset registration
  resetRegistration() {
    this.resetRecognition();
  }

  resetCurrentInvalidations():void{
    this.currentInvalidationsOnCapture.farAway = false;
    this.currentInvalidationsOnCapture.notCentered = false;
    this.currentInvalidationsOnCapture.veryExpressive = false;
  }

  isAGoodFrame():boolean{
    return !this.currentInvalidationsOnCapture.farAway && !this.currentInvalidationsOnCapture.notCentered && !this.currentInvalidationsOnCapture.veryExpressive;
  }

  isAGoodPosition():boolean{
    return !this.currentInvalidationsOnCapture.farAway && !this.currentInvalidationsOnCapture.notCentered;
  }
  
  close() {
    this.closeModal.emit(true);
  }

  cameraError(errorType:RecognitionErrors){
    this.errorType = errorType;
    this.recognitionStep = RecognitionSteps.RecognitionFailed;
  }

  calculateAverage(scores: number[]): number {
    const sum = scores.reduce((total, score) => total + score, 0);
    const average = sum / scores.length;
    return average;
  }
}
